<template>
  <div class="details-container">
    <div class="details-header">
      <h2>User Details</h2>
    </div>
    <div class="details-content">
      <div class="details-section">
        <h3>Personal Information</h3>
        <div class="details-group half-width">
          <label>First Name</label>
          <div class="details-value">{{ userData.first_name }}</div>
        </div>

        <div class="details-group half-width">
          <label>Last Name</label>
          <div class="details-value">{{ userData.last_name }}</div>
        </div>

        <div class="details-group half-width">
          <label>Email</label>
          <div class="details-value">
            <a :href="`mailto:${userData.email}`">
              {{ userData.email }}
            </a>
          </div>
          <div v-if="!userData.verified_email" class="verification-status">
            Not Verified
          </div>
        </div>

        <div class="details-group half-width">
          <label>Phone</label>
          <div class="details-value">{{ userData.phone }}</div>
        </div>
      </div>

      <div class="details-section" v-if="isSuperuser()">
        <h3>Organization Memberships</h3>
        <div class="details-group full-width">
          <div v-if="userOrganizations.length > 0" class="organizations-list">
            <template v-for="(org, index) in userOrganizations" :key="org.id">
              <router-link 
                :to="`/organizations/${org.id}`" 
                :title="`View Organization &quot;${org.name}&quot;`"
                class="link-button"
              >
                {{ org.name }}
                <RoleBadges :roles="org.member_roles" />
              </router-link>
              <br v-if="index < userOrganizations.length - 1">
            </template>
          </div>
          <div v-else class="details-value">No organizations found</div>
        </div>
      </div>

      <div class="details-section" v-if="isSuperuser()">
        <h3>Contact & Company Details</h3>
        <div class="details-group third-width">
          <label>Role / Title</label>
          <div class="details-value">{{ userData.role }}</div>
        </div>

        <div class="details-group two-thirds-width">
          <label>Company / Organization</label>
          <div class="details-value">{{ userData.company }}</div>
        </div>

        <div class="details-group third-width">
          <label>Phone</label>
          <div class="details-value">{{ userData.phone }}</div>
        </div>

        <div class="details-group two-thirds-width">
          <label>Website URL</label>
          <div class="details-value">
            <a v-if="userData.website_url" :href="userData.website_url">
              {{ userData.website_url }}
            </a>
          </div>
        </div>
        
        <div class="details-group full-width">
          <label>Address</label>
          <div class="details-value">{{ userData.address_line_1 }}</div>
        </div>

        <div class="details-group full-width">
          <div class="details-value">{{ userData.address_line_2 }}</div>
        </div>

        <div class="details-group half-width">
          <label>City</label>
          <div class="details-value">{{ userData.city }}</div>
        </div>

        <div class="details-group half-width">
          <label>State</label>
          <div class="details-value">{{ userData.state }}</div>
        </div>

        <div class="details-group half-width">
          <label>Country</label>
          <div class="details-value">{{ userData.country }}</div>
        </div>

        <div class="details-group half-width">
          <label>Postal Code</label>
          <div class="details-value">{{ userData.postal_code }}</div>
        </div>
      </div>

      <div class="details-actions">
        <router-link
          :to="`/users/${userId}/edit`"
          class="important-button big"
          v-if="canEdit"
        >
          Edit User
        </router-link>
        <button class="cancel-button medium" @click="goBack">Back</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { isSuperuser } from '@/scripts/permissions';
import RoleBadges from '@/components/RoleBadges.vue';
import axios from 'axios';

const route = useRoute();
const router = useRouter();

const userId = ref(null);
const userData = ref({});
const userOrganizations = ref([]);

const canEdit = computed(() => {
  return isSuperuser() || userId.value === 'me';
});

async function fetchUserData() {
  try {
    const response = await axios.get(`/api/users/${userId.value}`);
    userData.value = response.data;
  } catch (error) {
    console.error('Error fetching user data:', error);
  }
}

async function fetchUserOrganizations() {
  try {
    const response = await axios.get(`/api/users/${userId.value}/organizations`);
    userOrganizations.value = response.data;
  } catch (error) {
    console.error('Error fetching user organizations:', error);
  }
}

function goBack() {
  router.back();
}

onMounted(async () => {
  userId.value = route.params.user_id || 'me';
  await fetchUserData();
  if (isSuperuser()) {
    await fetchUserOrganizations();
  }
});
</script>

<style scoped>
@import '@/styles/shared-details.css';
@import '@/styles/organization-members.css';
</style>
